// Preloader: shows a loading state (keeps the E2E smoke check happy), loads the
// asset manifest, then waits for a tap to satisfy the Web Audio autoplay policy
// before entering the menu.

import type { App, Scene } from "../core/app";
import { PALETTE } from "../game/config";
import { drawLogo, text, drawButton, roundRect, registerFighterArt } from "../art/draw";
import { MenuScene } from "./menu";

export class PreloaderScene implements Scene {
  private t = 0;
  private frac = 0;
  private ready = false;

  constructor(private app: App) {}

  enter(): void {
    // Audio files fetch in parallel; they decode on the start tap (autoplay policy).
    this.app.audio.preload(this.app.assets.audioMap());
    // Warm the webfonts the canvas uses (Archivo weights + JetBrains Mono) —
    // canvas fillText does NOT trigger font loads by itself, so without this
    // the first banner/cut-in could render in the fallback font and repaint.
    const ff = (document as { fonts?: FontFaceSet }).fonts;
    if (ff) {
      for (const spec of ["700 16px Archivo", "800 24px Archivo", "900 40px Archivo", "700 14px 'JetBrains Mono'"]) {
        void ff.load(spec).catch(() => undefined);
      }
    }
    void this.app.assets.load((f) => (this.frac = f)).then(() => {
      registerFighterArt(this.app.assets); // fighters render from the manifest PNGs
      this.ready = true;
    });
  }

  update(dt: number): void {
    this.t += dt;
    if (this.ready) {
      const { app } = this;
      const r = this.startRect();
      if (app.input.clicked(r) || app.input.clickedAnywhere()) {
        app.audio.resume();
        app.audio.startMusic();
        app.setScene(new MenuScene(app));
      }
    }
  }

  private startRect() {
    return { x: this.app.w / 2 - 120, y: this.app.h * 0.66, w: 240, h: 56 };
  }

  render(): void {
    const { app } = this;
    const ctx = app.ctx;
    app.clear(PALETTE.bg);
    drawLogo(ctx, app.w / 2, app.h * 0.36, Math.min(app.w, app.h) * 0.06, app.assets.get("logo"), this.t);

    if (!this.ready) {
      // "loading…" — matches the smoke spec's expectation.
      text(ctx, "loading…", app.w / 2, app.h * 0.6, {
        size: 16,
        color: PALETTE.muted,
        mono: true,
        align: "center",
      });
      const bw = Math.min(260, app.w - 60);
      const bx = app.w / 2 - bw / 2;
      roundRect(ctx, bx, app.h * 0.63, bw, 8, 4);
      ctx.fillStyle = "#20202c";
      ctx.fill();
      ctx.fillStyle = PALETTE.purple;
      ctx.fillRect(bx, app.h * 0.63, bw * this.frac, 8);
    } else {
      const r = this.startRect();
      drawButton(ctx, r.x, r.y, r.w, r.h, "TAP TO START", {
        active: true,
        hover: app.input.hover(r),
        size: 22,
      });
      text(ctx, "Two roosters. One crown.", app.w / 2, app.h * 0.6, {
        size: 15,
        color: PALETTE.muted,
        align: "center",
      });
    }
  }
}
